import { EntityIconMap, EntityToneMap } from '@common/Entity/constants';
import { getSqlEntityUrl } from '@common/Entity/utils';
import { QueryKeys } from '@common/queries/common';
import { InMemorySearchQuery } from '@common/queries/lineage';
import { getTableId } from '@common/SQL/utils';
import { Entity, sqlMetadataEntityToEntity } from '@common/types/Entity';
import { getJobIdeUrl, Private_Routes } from '@common/url';
import { BackEndCodeLanguage, EntityKind, Search } from '@prophecy/graphql-types/enums';
import { ExtractArrayType } from '@prophecy/interfaces/generic';
import { BaseColor, theme } from '@prophecy/theme';
import {
  Stack,
  Button,
  Card,
  Text,
  Tags,
  StackItem,
  RadialIconContainer,
  Centered,
  Spinner,
  Badges
} from '@prophecy/ui-v3';
import { ResultOf } from 'gql.tada';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useInfiniteGraphQlQuery } from '../../../data/util';
import { LineageSearchAllResults, DEFAULT_PAGE_SIZE } from './contants';
import { AppliedFilters, LineageSearchEntitiesType } from './types';

type InMemorySearchResult = NonNullable<ResultOf<typeof InMemorySearchQuery>['InMemorySearch']>;
type InMemorySearchHit = ExtractArrayType<InMemorySearchResult['nameResults']>;

const StyledCard = styled(Card)`
  padding: ${theme.spaces.x12};
`;

const StyledTags = styled(Tags)`
  border-radius: 50%;
`;

const ColumnsWrapper = styled(Stack)`
  flex-wrap: wrap;
  margin-top: ${theme.spaces.x12};
`;

const LoadMoreButton = styled(Button)`
  padding: ${theme.spaces.x12};
` as unknown as typeof Button;
function SearchItemCard({
  entity,
  title,
  label,
  children
}: {
  entity: LineageSearchEntitiesType;
  title: string;
  label: string;
  children?: React.ReactNode;
}) {
  const tone = EntityToneMap[entity];
  const { icon: Icon, type: IconType } = EntityIconMap[entity];

  return (
    <StyledCard>
      <Stack direction='horizontal' gap={theme.spaces.x12} alignY='start'>
        <RadialIconContainer tone={tone}>
          {/** @ts-ignore TODO: fix icon type */}
          <Icon type={IconType} />
        </RadialIconContainer>
        <StackItem grow='1'>
          <Stack>
            <Text level='md' tone={theme.colors.gray700}>
              {title}
            </Text>
            <Text level='sm' tone={theme.colors.gray500}>
              {label}
            </Text>
            {children}
          </Stack>
        </StackItem>
      </Stack>
    </StyledCard>
  );
}

function LoadMoreFooter({
  isLoading,
  remaining,
  loadMore,
  loadedCount
}: {
  isLoading: boolean;
  remaining: number;
  loadedCount: number;
  loadMore: () => void;
}) {
  return (
    <div>
      {isLoading && (
        <Centered>
          <Spinner />
        </Centered>
      )}
      {remaining > 0 && !(isLoading && loadedCount === 0) && (
        <LoadMoreButton
          variant='link'
          disabled={isLoading}
          onClick={loadMore}
          data-test-id='lineage-search-result-load-more-button'>
          Load More <StyledTags>{remaining}</StyledTags>
        </LoadMoreButton>
      )}
    </div>
  );
}

function entityKindToEntity(entityKind: string): LineageSearchEntitiesType {
  if (entityKind === Entity.SqlOrchestratorPipeline) {
    return Entity.Pipeline;
  }
  return sqlMetadataEntityToEntity(entityKind) as LineageSearchEntitiesType;
}

function isSqlEntityResult(entityKind: string): boolean {
  const sqlEntityKinds: string[] = [
    EntityKind.SQLModel,
    EntityKind.SQLSeed,
    EntityKind.SQLSource,
    EntityKind.SQLTable,
    EntityKind.SQLMacro,
    EntityKind.SQLUnreferencedSource,
    EntityKind.SQLSnapshot,
    EntityKind.SQLTest,
    EntityKind.SQLBusinessApp,
    EntityKind.SQLOrchestratorPipeline
  ];
  return sqlEntityKinds.includes(entityKind);
}

function getSparkEntityId(entityId: string, projectId: string) {
  if (entityId.startsWith(`${projectId}/`)) return entityId;
  return `${projectId}/${entityId}`;
}

function getEntityIdForUI(result: InMemorySearchHit): string {
  const { entityId, entityKind, name, sourceName, projectId } = result;
  const isSqlEntity = isSqlEntityResult(entityKind);

  if (!isSqlEntity) return getSparkEntityId(entityId, projectId.toString());
  const entity = entityKindToEntity(entityKind);

  if (entity === Entity.Table || entity === Entity.Seed) {
    return getTableId({
      tableType: entity,
      sourceName: sourceName || '',
      name
    });
  }

  return entityId;
}

function getEntitySearchResultUrl(result: InMemorySearchHit): string {
  const { entityKind, name, branch } = result;
  const entityId = getEntityIdForUI(result);
  const entity = entityKindToEntity(entityKind);
  const isSqlEntity = isSqlEntityResult(entityKind);
  const projectId = result.projectId.toString();

  if (entity === Entity.Job) {
    return getJobIdeUrl(entityId, projectId, BackEndCodeLanguage.python, branch);
  }

  if (isSqlEntity) {
    return getSqlEntityUrl({
      entityId,
      entityName: name,
      projectId: String(projectId),
      entityKind: entity,
      branch
    });
  }

  return Private_Routes.Lineage_IDE.datasetOrPipeline.getUrl({
    projectId: projectId,
    type: entity.toLowerCase(),
    datasetOrPipelineId: entityId
  });
}

function getSearchVariables(appliedFilters: AppliedFilters, searchStr: string, offset: number) {
  const isAllSelected = appliedFilters.entityKinds.includes(LineageSearchAllResults);

  let entityKinds: EntityKind[] | undefined;
  if (!isAllSelected) {
    entityKinds = appliedFilters.entityKinds.flatMap((kind) => {
      // When Pipeline is selected, also include SQLOrchestratorPipeline
      if (kind === EntityKind.Pipeline) {
        return [EntityKind.Pipeline, EntityKind.SQLOrchestratorPipeline];
      }
      return kind as EntityKind;
    });
  }

  return {
    query: searchStr,
    entityKinds,
    projectIds: appliedFilters.projectIds.length > 0 ? appliedFilters.projectIds : undefined,
    offset,
    limit: DEFAULT_PAGE_SIZE
  };
}

type InMemorySearchResponseData = {
  InMemorySearch?: {
    nameResults?: InMemorySearchHit[];
    columnResults?: InMemorySearchHit[];
    totalNameResultsCount?: number;
    totalColumnResultsCount?: number;
  };
};

export type SearchResultsData = {
  results: InMemorySearchHit[];
  totalCount: number;
};

export type LineageSearchReturn = {
  entityResults: SearchResultsData;
  columnResults: SearchResultsData;
  isLoading: boolean;
  loadMore: () => void;
};

export function useLineageSearch(searchStr: string, appliedFilters: AppliedFilters): LineageSearchReturn {
  const variables = getSearchVariables(appliedFilters, searchStr, 0);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteGraphQlQuery(
    [QueryKeys.LineageSearch, variables],
    InMemorySearchQuery,
    {
      refetchOnWindowFocus: false,
      getNextPageParam: (lastPage, allPages) => {
        const page = lastPage as InMemorySearchResponseData;
        const currentOffset = allPages.length * DEFAULT_PAGE_SIZE;
        const totalNameCount = page?.InMemorySearch?.totalNameResultsCount ?? 0;
        const totalColumnCount = page?.InMemorySearch?.totalColumnResultsCount ?? 0;
        const maxTotal = Math.max(totalNameCount, totalColumnCount);

        if (currentOffset < maxTotal) {
          return { offset: currentOffset };
        }
        return undefined;
      }
    },
    variables
  );

  const { nameResults, columnResults, totalNameCount, totalColumnCount } = useMemo(() => {
    let nameResults: InMemorySearchHit[] = [];
    let columnResults: InMemorySearchHit[] = [];
    let totalNameCount = 0;
    let totalColumnCount = 0;

    data?.pages.forEach((pageData, index) => {
      const page = pageData as InMemorySearchResponseData;
      if (page?.InMemorySearch?.nameResults) {
        nameResults = nameResults.concat(page.InMemorySearch.nameResults);
      }
      if (page?.InMemorySearch?.columnResults) {
        columnResults = columnResults.concat(page.InMemorySearch.columnResults);
      }
      if (index === 0) {
        totalNameCount = page?.InMemorySearch?.totalNameResultsCount ?? 0;
        totalColumnCount = page?.InMemorySearch?.totalColumnResultsCount ?? 0;
      }
    });

    return { nameResults, columnResults, totalNameCount, totalColumnCount };
  }, [data]);

  const loadMore = () => {
    fetchNextPage();
  };

  return {
    entityResults: {
      results: nameResults,
      totalCount: totalNameCount
    },
    columnResults: {
      results: columnResults,
      totalCount: totalColumnCount
    },
    isLoading: isLoading || isFetchingNextPage,
    loadMore
  };
}

type EntityMatchProps = {
  results: InMemorySearchHit[];
  totalCount: number;
  isLoading: boolean;
  loadMore: () => void;
};

export function EntityMatch({ results, totalCount, isLoading, loadMore }: EntityMatchProps) {
  const loadedCount = results.length;

  return (
    <Stack gap={theme.spaces.x12}>
      <Stack gap={theme.sizes.x16} padding={theme.spaces.x12}>
        {results.map((result, index) => {
          const { entityKind, name, projectName } = result;
          const entity = entityKindToEntity(entityKind);

          return (
            <Link key={index} to={getEntitySearchResultUrl(result)}>
              <SearchItemCard entity={entity} label={projectName} title={name} />
            </Link>
          );
        })}
      </Stack>
      <LoadMoreFooter
        isLoading={isLoading}
        remaining={totalCount - loadedCount}
        loadedCount={loadedCount}
        loadMore={loadMore}
      />
    </Stack>
  );
}

type ColumnMatchProps = {
  results: InMemorySearchHit[];
  totalCount: number;
  isLoading: boolean;
  loadMore: () => void;
};

export function ColumnMatch({ results, totalCount, isLoading, loadMore }: ColumnMatchProps) {
  const loadedCount = results.length;

  return (
    <Stack gap={theme.spaces.x12}>
      <Stack gap={theme.sizes.x16} padding={theme.spaces.x12}>
        {results.map((result, index) => {
          const { entityKind, name, projectName, matchedTerms, branch } = result;
          const projectId = result.projectId.toString();
          const entity = entityKindToEntity(entityKind);
          const isSqlEntity = isSqlEntityResult(entityKind);
          const entityId = getEntityIdForUI(result);

          const columnNames = matchedTerms.filter((term) => term.searchType === Search.Column).map((term) => term.term);

          return (
            <SearchItemCard key={index} entity={entity} label={projectName} title={name}>
              <ColumnsWrapper direction='horizontal' gap={theme.spaces.x12}>
                {columnNames.map((colName, colIndex) => {
                  const url = isSqlEntity
                    ? getSqlEntityUrl({
                        entityId,
                        entityKind: entity,
                        entityName: name,
                        projectId,
                        branch
                      })
                    : Private_Routes.Lineage_IDE.column.getUrl({
                        projectId,
                        type: entity.toLowerCase(),
                        datasetOrPipelineId: entityId,
                        dataset: entityId,
                        column: colName
                      });

                  return (
                    <Badges key={colIndex} tone={BaseColor.gray} elementType='link' to={url}>
                      {colName}
                    </Badges>
                  );
                })}
              </ColumnsWrapper>
            </SearchItemCard>
          );
        })}
      </Stack>
      <LoadMoreFooter
        isLoading={isLoading}
        remaining={totalCount - loadedCount}
        loadedCount={loadedCount}
        loadMore={loadMore}
      />
    </Stack>
  );
}
